/**
 * Confirmation Page Types
 *
 * Task: T-P4-D2-F6-FE-2
 *
 * Types for the multi-view confirmation page that displays different UIs
 * based on booking status and service configuration.
 */

/**
 * The possible view states for the confirmation page.
 *
 * View determination priority:
 * 1. Booking status (if booking exists)
 * 2. Service configuration (requiresApproval, paymentRequirement)
 * 3. Default (REVIEW_DIRECT)
 */
export type ConfirmationView =
  | 'REVIEW_DIRECT' // No approval, no online payment - direct confirm
  | 'REVIEW_PAYMENT' // No approval, online payment required (DEPOSIT/FULL_PAYMENT)
  | 'REVIEW_APPROVAL' // Requires approval before booking
  | 'PENDING' // Booking submitted, awaiting approval
  | 'CONFIRMED' // Booking confirmed
  | 'CANCELLED' // Unpaid pending request cancelled or declined
  | 'CANCELLED_BOOKING' // Cancelled booking whose payment history needs neutral copy
  | 'AWAITING_PAYMENT'; // Admin approved, awaiting payment (redirect case)

/**
 * Button action types for the primary CTA button
 */
export type ButtonAction = 'confirm' | 'payment' | 'submit' | 'done' | 'redirect';

/**
 * Configuration for each view state, used by the ConfirmationPage component
 * to render the appropriate UI.
 */
export interface ViewConfig {
  /** The view type this config applies to */
  view: ConfirmationView;

  /** Page title displayed in the header */
  title: string;

  /** Optional subtitle displayed below the title */
  subtitle?: string;

  /** Text for the primary action button */
  buttonText: string;

  /** Action to perform when button is clicked */
  buttonAction: ButtonAction;

  /** Show animated success checkmark (for CONFIRMED view) */
  showSuccessAnimation?: boolean;

  /** Show animated pending/clock icon (for PENDING view) */
  showPendingAnimation?: boolean;
}

/**
 * Known booking statuses that map to confirmation views.
 * Used for type narrowing from string.
 */
export const KNOWN_BOOKING_STATUSES = [
  'CONFIRMED',
  'PENDING_APPROVAL',
  'PENDING_PAYMENT',
  'CANCELLED',
] as const;

export type KnownBookingStatus = (typeof KNOWN_BOOKING_STATUSES)[number];

/**
 * Type guard to check if a status is a known booking status
 */
export function isKnownBookingStatus(
  status: string | undefined,
): status is KnownBookingStatus {
  return KNOWN_BOOKING_STATUSES.includes(status as KnownBookingStatus);
}

/**
 * Payment requirements that require online payment step
 */
export const ONLINE_PAYMENT_REQUIREMENTS = ['DEPOSIT', 'FULL_PAYMENT'] as const;

export type OnlinePaymentRequirement = (typeof ONLINE_PAYMENT_REQUIREMENTS)[number];

/**
 * Type guard to check if payment requirement needs online payment
 */
export function requiresOnlinePayment(
  requirement: string | undefined,
): requirement is OnlinePaymentRequirement {
  return ONLINE_PAYMENT_REQUIREMENTS.includes(
    requirement as OnlinePaymentRequirement,
  );
}
